import React, { useState, useEffect } from 'react';
import { 
  Shield, Eye, EyeOff, Check, Lock, AlertCircle, 
  Smartphone, Copy, CheckCircle, RefreshCw, X, Key,
  AlertTriangle, Loader
} from 'lucide-react';
import api from '../../services/api';
import { mfaService } from '../../services/mfaService';
import Modal from '../Modal';
import { QRCodeSVG } from 'qrcode.react';

const SecurityTab = ({ showToast }) => {
  // ============================================
  // CHANGE PASSWORD STATE
  // ============================================
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});

  // ============================================
  // MFA STATE
  // ============================================
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(true);
  
  // Setup flow
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupStep, setSetupStep] = useState(1); // 1: QR, 2: Verify, 3: Recovery Codes
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Disable flow
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableForm, setDisableForm] = useState({ password: '', code: '' });
  const [disabling, setDisabling] = useState(false);

  // Regenerate recovery codes
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regeneratePassword, setRegeneratePassword] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  // ============================================
  // LOAD MFA STATUS
  // ============================================
  useEffect(() => {
    loadMfaStatus();
  }, []);

  const loadMfaStatus = async () => {
    try {
      setMfaLoading(true);
      const status = await mfaService.getStatus();
      setMfaStatus(status);
    } catch (error) {
      console.error('Error loading MFA status:', error);
    } finally {
      setMfaLoading(false);
    }
  };

  // ============================================
  // PASSWORD FUNCTIONS
  // ============================================
  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 1) return { score: 1, label: 'Weak', color: 'bg-red-500' };
    if (score <= 2) return { score: 2, label: 'Fair', color: 'bg-orange-500' };
    if (score <= 3) return { score: 3, label: 'Good', color: 'bg-yellow-500' };
    if (score <= 4) return { score: 4, label: 'Strong', color: 'bg-green-500' };
    return { score: 5, label: 'Very Strong', color: 'bg-green-600' };
  };

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordForm.currentPassword) errors.currentPassword = 'Current password is required';
    if (!passwordForm.newPassword) errors.newPassword = 'New password is required';
    else if (passwordForm.newPassword.length < 8) errors.newPassword = 'Password must be at least 8 characters';
    if (!passwordForm.confirmPassword) errors.confirmPassword = 'Please confirm your new password';
    else if (passwordForm.newPassword !== passwordForm.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (passwordForm.currentPassword && passwordForm.newPassword && passwordForm.currentPassword === passwordForm.newPassword) {
      errors.newPassword = 'New password must be different from current password';
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    try {
      setChangingPassword(true);
      await api.post('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
      showToast('Password changed successfully!', 'success');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to change password';
      showToast(message, 'error');
      if (message.toLowerCase().includes('current password')) {
        setPasswordErrors({ currentPassword: message });
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // ============================================
  // MFA SETUP FUNCTIONS
  // ============================================
  const handleBeginSetup = async () => {
    try {
      const data = await mfaService.beginSetup();
      setSetupData(data);
      setSetupStep(1);
      setVerifyCode('');
      setShowSetupModal(true);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to start MFA setup', 'error');
    }
  };

  const handleVerifySetup = async () => {
    if (verifyCode.length !== 6) {
      showToast('Please enter a 6-digit code', 'error');
      return;
    }

    try {
      setVerifying(true);
      const result = await mfaService.verifySetup(setupData.secret, verifyCode);
      setRecoveryCodes(result.recoveryCodes);
      setSetupStep(3);
      await loadMfaStatus();
      showToast('MFA enabled successfully!', 'success');
    } catch (error) {
      showToast(error.response?.data?.message || 'Invalid code. Please try again.', 'error');
    } finally {
      setVerifying(false);
    }
  };

  const handleCopyRecoveryCodes = () => {
    const codesText = recoveryCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  const handleCloseSetupModal = () => {
    if (setupStep === 3) {
      // Make sure they've seen the recovery codes
      if (!copiedCodes && !window.confirm('Have you saved your recovery codes? You won\'t be able to see them again.')) {
        return;
      }
    }
    setShowSetupModal(false);
    setSetupData(null);
    setVerifyCode('');
    setRecoveryCodes([]);
    setSetupStep(1);
    setCopiedCodes(false);
  };

  // ============================================
  // MFA DISABLE FUNCTIONS
  // ============================================
  const handleDisableMfa = async () => {
    if (!disableForm.password || !disableForm.code) {
      showToast('Please enter your password and verification code', 'error');
      return;
    }

    try {
      setDisabling(true);
      await mfaService.disable(disableForm.password, disableForm.code);
      setShowDisableModal(false);
      setDisableForm({ password: '', code: '' });
      await loadMfaStatus();
      showToast('MFA has been disabled', 'success');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to disable MFA', 'error');
    } finally {
      setDisabling(false);
    }
  };

  // ============================================
  // REGENERATE RECOVERY CODES
  // ============================================
  const handleRegenerateCodes = async () => {
    if (!regeneratePassword) {
      showToast('Please enter your password', 'error');
      return;
    }

    try {
      setRegenerating(true);
      const result = await mfaService.regenerateRecoveryCodes(regeneratePassword);
      setRecoveryCodes(result.recoveryCodes);
      setShowRegenerateModal(false);
      setRegeneratePassword('');
      setSetupStep(3);
      setShowSetupModal(true);
      await loadMfaStatus();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to regenerate codes', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  const passwordStrength = getPasswordStrength(passwordForm.newPassword);

  return (
    <div className="space-y-6">
      {/* ============================================ */}
      {/* CHANGE PASSWORD SECTION */}
      {/* ============================================ */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Lock className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Change Password</h2>
            <p className="text-sm text-gray-600">Update your account password</p>
          </div>
        </div>

        <form onSubmit={handleChangePassword} className="max-w-md space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordForm.currentPassword}
                onChange={(e) => {
                  setPasswordForm({ ...passwordForm, currentPassword: e.target.value });
                  if (passwordErrors.currentPassword) setPasswordErrors({ ...passwordErrors, currentPassword: null });
                }}
                className={`input pr-10 ${passwordErrors.currentPassword ? 'border-red-500' : ''}`}
                placeholder="Enter current password"
              />
              <button type="button" onClick={() => togglePasswordVisibility('current')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {passwordErrors.currentPassword && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{passwordErrors.currentPassword}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordForm.newPassword}
                onChange={(e) => {
                  setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                  if (passwordErrors.newPassword) setPasswordErrors({ ...passwordErrors, newPassword: null });
                }}
                className={`input pr-10 ${passwordErrors.newPassword ? 'border-red-500' : ''}`}
                placeholder="Enter new password"
              />
              <button type="button" onClick={() => togglePasswordVisibility('new')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {passwordErrors.newPassword && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{passwordErrors.newPassword}</p>
            )}
            {passwordForm.newPassword && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{ width: `${(passwordStrength.score / 5) * 100}%` }} />
                  </div>
                  <span className={`text-xs font-medium ${passwordStrength.score <= 2 ? 'text-red-600' : passwordStrength.score <= 3 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {passwordStrength.label}
                  </span>
                </div>
                <p className="text-xs text-gray-500">Use 8+ characters with uppercase, lowercase, numbers & symbols</p>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordForm.confirmPassword}
                onChange={(e) => {
                  setPasswordForm({ ...passwordForm, confirmPassword: e.target.value });
                  if (passwordErrors.confirmPassword) setPasswordErrors({ ...passwordErrors, confirmPassword: null });
                }}
                className={`input pr-10 ${passwordErrors.confirmPassword ? 'border-red-500' : ''}`}
                placeholder="Confirm new password"
              />
              <button type="button" onClick={() => togglePasswordVisibility('confirm')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {passwordErrors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><AlertCircle size={14} />{passwordErrors.confirmPassword}</p>
            )}
            {passwordForm.confirmPassword && passwordForm.newPassword === passwordForm.confirmPassword && (
              <p className="mt-1 text-sm text-green-600 flex items-center gap-1"><Check size={14} />Passwords match</p>
            )}
          </div>

          <div className="pt-4">
            <button type="submit" disabled={changingPassword} className="btn btn-primary">
              {changingPassword ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      {/* ============================================ */}
      {/* MFA SECTION */}
      {/* ============================================ */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Smartphone className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Two-Factor Authentication</h2>
            <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
          </div>
        </div>

        {mfaLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="w-6 h-6 text-purple-600 animate-spin" />
          </div>
        ) : mfaStatus?.enabled ? (
          // MFA ENABLED STATE
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800">Two-factor authentication is enabled</p>
                <p className="text-sm text-green-700">
                  Enabled on {new Date(mfaStatus.enabledAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            {mfaStatus.recoveryCodesRemaining <= 3 && (
              <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-yellow-800">
                    Only {mfaStatus.recoveryCodesRemaining} recovery code{mfaStatus.recoveryCodesRemaining !== 1 ? 's' : ''} remaining
                  </p>
                  <p className="text-sm text-yellow-700">Consider generating new recovery codes</p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowRegenerateModal(true)}
                className="btn btn-secondary flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Regenerate Recovery Codes
              </button>
              <button
                onClick={() => setShowDisableModal(true)}
                className="btn border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-2"
              >
                <X size={16} />
                Disable MFA
              </button>
            </div>
          </div>
        ) : (
          // MFA DISABLED STATE
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-700 mb-3">
                Two-factor authentication adds an extra layer of security by requiring a code from your 
                phone in addition to your password when logging in.
              </p>
              <p className="text-sm text-gray-600">
                You'll need an authenticator app like Google Authenticator, Authy, or 1Password.
              </p>
            </div>

            <button
              onClick={handleBeginSetup}
              className="btn btn-primary flex items-center gap-2"
            >
              <Shield size={18} />
              Enable Two-Factor Authentication
            </button>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* MFA SETUP MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showSetupModal}
        onClose={handleCloseSetupModal}
        title={setupStep === 3 ? 'Save Your Recovery Codes' : 'Set Up Two-Factor Authentication'}
        size="md"
      >
        {setupStep === 1 && setupData && (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                Scan this QR code with your authenticator app
              </p>
              <div className="inline-block p-4 bg-white border rounded-lg">
                <QRCodeSVG value={setupData.qrCodeUrl} size={200} />
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Can't scan? Enter this code manually:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono break-all">
                  {setupData.manualEntryKey}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(setupData.secret);
                    showToast('Code copied!', 'success');
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <Copy size={18} />
                </button>
              </div>
            </div>

            <button
              onClick={() => setSetupStep(2)}
              className="btn btn-primary w-full"
            >
              Continue
            </button>
          </div>
        )}

        {setupStep === 2 && (
          <div className="space-y-6">
            <p className="text-gray-600">
              Enter the 6-digit code from your authenticator app to verify setup.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="input text-center text-2xl tracking-widest font-mono"
                placeholder="000000"
                maxLength={6}
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSetupStep(1)}
                className="btn btn-secondary flex-1"
              >
                Back
              </button>
              <button
                onClick={handleVerifySetup}
                disabled={verifying || verifyCode.length !== 6}
                className="btn btn-primary flex-1"
              >
                {verifying ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </div>
        )}

        {setupStep === 3 && recoveryCodes.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              <p className="text-sm text-yellow-800">
                <strong>Save these codes!</strong> You won't be able to see them again. 
                Use them to log in if you lose access to your authenticator.
              </p>
            </div>

            <div className="bg-gray-900 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-2">
                {recoveryCodes.map((code, index) => (
                  <code key={index} className="text-green-400 font-mono text-sm">
                    {code}
                  </code>
                ))}
              </div>
            </div>

            <button
              onClick={handleCopyRecoveryCodes}
              className={`btn w-full flex items-center justify-center gap-2 ${
                copiedCodes ? 'btn-primary' : 'btn-secondary'
              }`}
            >
              {copiedCodes ? (
                <>
                  <CheckCircle size={18} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={18} />
                  Copy Recovery Codes
                </>
              )}
            </button>

            <button
              onClick={handleCloseSetupModal}
              className="btn btn-primary w-full"
            >
              I've Saved My Codes
            </button>
          </div>
        )}
      </Modal>

      {/* ============================================ */}
      {/* DISABLE MFA MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showDisableModal}
        onClose={() => {
          setShowDisableModal(false);
          setDisableForm({ password: '', code: '' });
        }}
        title="Disable Two-Factor Authentication"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              Disabling MFA will make your account less secure.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={disableForm.password}
              onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })}
              className="input"
              placeholder="Enter your password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Verification Code
            </label>
            <input
              type="text"
              value={disableForm.code}
              onChange={(e) => setDisableForm({ ...disableForm, code: e.target.value })}
              className="input"
              placeholder="6-digit code or recovery code"
            />
            <p className="text-xs text-gray-500 mt-1">Enter code from authenticator or a recovery code</p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => {
                setShowDisableModal(false);
                setDisableForm({ password: '', code: '' });
              }}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              onClick={handleDisableMfa}
              disabled={disabling || !disableForm.password || !disableForm.code}
              className="btn bg-red-600 text-white hover:bg-red-700 flex-1"
            >
              {disabling ? 'Disabling...' : 'Disable MFA'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ============================================ */}
      {/* REGENERATE RECOVERY CODES MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showRegenerateModal}
        onClose={() => {
          setShowRegenerateModal(false);
          setRegeneratePassword('');
        }}
        title="Regenerate Recovery Codes"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-800">
              This will invalidate all existing recovery codes.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={regeneratePassword}
              onChange={(e) => setRegeneratePassword(e.target.value)}
              className="input"
              placeholder="Enter your password"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => {
                setShowRegenerateModal(false);
                setRegeneratePassword('');
              }}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              onClick={handleRegenerateCodes}
              disabled={regenerating || !regeneratePassword}
              className="btn btn-primary flex-1"
            >
              {regenerating ? 'Generating...' : 'Generate New Codes'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SecurityTab;